/**
 * **La victoire** — l'écran que personne ne voyait.
 *
 * ## Le trou qu'il bouche
 *
 * Le routeur n'aiguillait vers un écran de fin que sur `status === "game_over"`. Or la
 * victoire ne pose pas `game_over` : elle pose `victory_locked` puis `status = "won"`, et
 * `clore_run` seul mène à `ended`. Conséquence exacte, mesurable : **un joueur qui atteint le
 * million voyait la pop-up du palier 1 000 000 et restait dans sa chambre.** L'objectif de
 * toute la partie ne s'affichait jamais.
 *
 * Cet écran est donc **routé sur l'état**, pas sur l'événement du tour : `status === "won"` et
 * `flags.apres_la_rue` encore faux. Un F5 juste après le million ramène ici — la question
 * « clore ou continuer » ne se perd pas, et c'est justement celle qu'on ne peut pas rater.
 *
 * ## Ce que les deux boutons font vraiment
 *
 * Ils sont là parce que le moteur les porte **enfin** : `clore_run` pose `status = "ended"`,
 * `continuer_apres_rue` pose `flags.apres_la_rue` — le drapeau sans lequel `appliquerProgression`
 * refuse les paliers 2M/5M/10M. Tant qu'ils n'avaient aucun effet, la session précédente a eu
 * raison de ne pas poser de bouton : il aurait menti.
 *
 * **La victoire est gravée** (PLAN §4.13, moteur §9.4) : ni l'un ni l'autre ne touche
 * `victory_locked` ni `ending`. Fermer sa run n'est pas la perdre, et mourir après le million
 * non plus — c'est la « mort en légende ».
 *
 * ⚠️ **Ce que cet écran ne sait PAS encore, et ne fait donc pas semblant de savoir** : laquelle
 * des trois fins (La Rue / CEO / La Communauté) est la sienne. `state.ending` reste `null` —
 * le moteur ne la détermine pas (M3.9, c'est écrit dans `progressionEtFins`). On l'affiche
 * quand elle existe, on le dit quand elle n'existe pas.
 *
 * ⟦C57 · lot 7 · A-174⟧ **Moins de texte, mais la récompense reste** (Matt, 02/10 : dans les fins,
 * la narration et l'épitaphe RESTENT à l'écran). Restent donc : le récit du million, « Ta fin » et
 * son texte, l'arc de rédemption (des blocs `data-narration`, que la mesure range à part). Passent
 * derrière « Les dessous » : le calcul du score (les cinq lignes de 200), ce que font les deux suites
 * (continuer, refermer) et le cas où le moteur ne nomme pas la fin. Les deux dialogues de confirmation
 * ne portent plus qu'une phrase chacun ; leur texte d'origine, mot pour mot, est dans les dessous.
 * Les textes vivent dans `game/fins.ts` (`RECITS_DE_VICTOIRE`, `texteClore`...).
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { OBJECTIF, PALIERS_POST_1M } from "@maxime/shared";

import { classement } from "../../api/jeu";
import { EcranFenetre } from "../../components/EcranFenetre";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { PlaqueCanvas } from "../../components/PlaqueCanvas";
import { Ceremonie } from "./Ceremonie";
import { arcDeRedemption, tracesDeLaChute } from "./redemption";
import { contenu } from "../../game/content";
import {
  NOMS_SCORE,
  PHRASE_CONTINUER,
  PHRASE_FIN_NON_NOMMEE,
  RECITS_DE_VICTOIRE,
  TEXTE_FIN_NON_NOMMEE,
  TEXTE_SCORE_ABSENT,
  phraseClore,
  texteClore,
  texteContinuerApresLaRue,
} from "../../game/fins";
import { jourSimule, nombre, nombreCourt } from "../../game/format";
import { useRunStore } from "../../stores/runStore";
import { dialog } from "../../stores/uiStore";

/*
 * ⟦C57 · lot 7⟧ Les trois fins de PLAN §4.13 (`RECITS_DE_VICTOIRE`) et les noms des cinq ingrédients
 * du score (`NOMS_SCORE`) vivent dans `game/fins.ts` : le Journal lit les mêmes textes que l'écran.
 */

/**
 * ⟦A-71⟧ Les runs dont la cérémonie a déjà été jouée.
 *
 * Pourquoi persister : cet écran est routé sur l'**état** (`status === "won"`), donc un F5 y
 * revient — et une cinématique de dix-neuf secondes qu'on se reprend à chaque rechargement
 * cesse très vite d'être une récompense. Pourquoi `localStorage` et pas le serveur : c'est un
 * confort d'affichage, pas une donnée de partie ; le moteur reste pur et l'API n'a pas à
 * connaître ce que le joueur a regardé.
 *
 * ⚠️ Et parce qu'une mémoire d'interface ne doit jamais **retirer** quelque chose au joueur, le
 * bouton « revoir » est là quoi qu'il arrive : le stockage indisponible, la clé effacée, un
 * autre navigateur — dans tous les cas la cérémonie reste à un clic.
 */
const CLE_CEREMONIE = "maxime.ceremonie.vue";

function ceremonieDejaVue(runId: string): boolean {
  try {
    const brut = localStorage.getItem(CLE_CEREMONIE);
    return brut !== null && (JSON.parse(brut) as unknown[]).includes(runId);
  } catch {
    // Stockage refusé (navigation privée, quota) : on rejoue la cérémonie. Montrer deux fois
    // vaut mieux que ne montrer jamais.
    return false;
  }
}

function marquerCeremonieVue(runId: string): void {
  try {
    const brut = localStorage.getItem(CLE_CEREMONIE);
    const vues = brut === null ? [] : (JSON.parse(brut) as string[]);
    if (!vues.includes(runId)) {
      // On ne garde que les vingt dernières : cette clé ne doit pas grossir avec le temps.
      localStorage.setItem(CLE_CEREMONIE, JSON.stringify([...vues, runId].slice(-20)));
    }
  } catch {
    /* pas de mémoire, pas de drame : la cérémonie se rejouera au prochain F5 */
  }
}

export function Victoire() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);
  const victoire = useRunStore((s) => s.victoire);
  const naviguer = useNavigate();

  /**
   * ⟦SPEC-UI §3.7⟧ Le rang gravé sur la plaque. Il est **déjà là** au moment où cet écran
   * s'ouvre : `progression.ts` insère la ligne de classement dans la même transaction que le
   * million (`status = won_continuing`), pas à la fermeture de la run.
   *
   * `null` tant qu'on ne l'a pas — et il reste `null` en bac à sable (aucun serveur) ou si la
   * run est hors de la page renvoyée. La plaque **ne dessine pas** la ligne dans ce cas : elle
   * n'invente pas un « #— ». Un échec de chargement ne dit rien non plus : c'est une décoration
   * de plaque, pas une information dont l'écran dépend.
   */
  const [rang, setRang] = useState<number | null>(null);
  const bacASable = run?.bacASable ?? true;
  const nomChaine = run?.channelName ?? "";
  const mode = run?.mode;
  const score = victoire?.score ?? null;

  useEffect(() => {
    if (bacASable || !mode) return;
    let vivant = true;
    classement({ mode })
      .then(({ entries }) => {
        if (!vivant) return;
        // On identifie la run par ce que le classement expose : son nom et son score. Deux runs
        // homonymes au même score seraient indiscernables — et le rang serait le même.
        const mien = entries.find(
          (e) => e.name === nomChaine && (score === null || e.score_total === score),
        );
        setRang(mien?.rank ?? null);
      })
      .catch(() => {
        /* pas de rang, pas de ligne : voir le commentaire ci-dessus */
      });
    return () => {
      vivant = false;
    };
  }, [bacASable, mode, nomChaine, score]);

  /**
   * ⟦A-71⟧ La cérémonie. Trois états seulement : `auto` (on décide en regardant la mémoire),
   * `joue` (le joueur a demandé à la revoir), `finie` (elle vient de se terminer ou d'être
   * passée). Aucun des trois ne peut empêcher la plaque d'apparaître ensuite.
   */
  const runId = run?.runId ?? "";
  const [etatCeremonie, setEtatCeremonie] = useState<"auto" | "joue" | "finie">("auto");
  // ⟦C61⟧ L'écran du million arrive en fondu quand il suit la cérémonie (qui s'est elle-même fondue
  // vers le fond nuit) : jamais de coupe sèche d'un écran à l'autre.
  const [apresCeremonie, setApresCeremonie] = useState(false);
  const dejaVue = useMemo(() => (runId === "" ? true : ceremonieDejaVue(runId)), [runId]);
  const joueLaCeremonie =
    runId !== "" && (etatCeremonie === "joue" || (etatCeremonie === "auto" && !dejaVue));

  if (!run) return null;
  const state = run.state;
  // ⟦C62 · A-179 · bug du million⟧ Le million est GRAVÉ au franchissement (le moteur retient le pic de l'action), mais les
  // désabonnés de la même nuit peuvent ramener le compteur sous la barre : une chaîne posée à pile 1 000 000 dormait et
  // se relisait « 999 881 abonnés » sous « Une rue porte ton nom ». L'écran de la victoire dit ce qui a été gravé.
  const abonnesGraves = Math.max(state.subscribers, OBJECTIF);

  if (joueLaCeremonie) {
    return (
      <Ceremonie
        nomChaine={run.channelName}
        fin={state.ending}
        genre={state.genre}
        onFini={() => {
          marquerCeremonieVue(runId);
          setApresCeremonie(true);
          setEtatCeremonie("finie");
        }}
      />
    );
  }
  const fin = state.ending ? RECITS_DE_VICTOIRE[state.ending] : null;

  // Le 100 % post-victoire, c'est l'arbre entier (PLAN §4.13). Le nombre de nœuds se COMPTE
  // dans le contenu — l'écrire « 39 » ici en ferait un mensonge au premier nœud ajouté.
  const noeudsTotal = contenu().skills.length;
  const noeudsPris = state.skills.length;

  async function clore() {
    // ⟦C57 · lot 7⟧ Une phrase : la conséquence qui compte. Le texte d'origine est dans les dessous.
    const ok = await dialog.confirm({
      title: "Refermer la chaîne ici ?",
      body: phraseClore(run?.channelName ?? ""),
      confirmLabel: "Je referme",
      cancelLabel: "Pas encore",
    });
    if (!ok) return;
    await act({ type: "clore_run" });
  }

  async function continuer() {
    const ok = await dialog.confirm({
      title: "Continuer après la rue ?",
      body: PHRASE_CONTINUER,
      confirmLabel: "Je continue",
      cancelLabel: "Rester là",
    });
    if (!ok) return;
    const resultat = await act({ type: "continuer_apres_rue" });
    if (resultat) naviguer("/", { replace: true });
  }

  // ⟦C57 · lot 7⟧ Ce que l'écran ne dit plus, mot pour mot : les deux suites, le calcul du score, et
  // le cas où le moteur ne nomme pas encore la fin.
  const dessous = (
    <LesDessous titre="Le million, en détail">
      <DessousBloc titre="Continuer après la rue">
        <p className="dessous-texte">{texteContinuerApresLaRue(noeudsTotal)}</p>
      </DessousBloc>
      <DessousBloc titre="Refermer la chaîne">
        <p className="dessous-texte">{texteClore(run.channelName, noeudsTotal - noeudsPris)}</p>
      </DessousBloc>
      <DessousBloc titre="Le score">
        {victoire?.score != null ? (
          <>
            <p className="dessous-texte">
              Score gravé : <strong>{nombre(victoire.score)}</strong> / 1 000.
            </p>
            {/* ⟦SPEC-UI §3.7 · A-74⟧ Les cinq lignes de 200 points (⟦A-14 §3⟧, poids égaux), calculées
                et transportées depuis la gravure. Principe ⟦A-16⟧ : montrer le calcul, jamais le
                solde. Après un F5 la décomposition ne revoyage pas (elle appartient au tour du
                million) : le bloc disparaît, le total reste, on n'invente rien. */}
            {victoire.decomposition ? (
              <ul className="score-lignes">
                {victoire.decomposition.lignes.map((l) => (
                  <li key={l.id} className="score-ligne">
                    <span className="score-nom">{NOMS_SCORE[l.id] ?? l.id}</span>
                    <span className="score-barre" aria-hidden="true">
                      <span className="score-rempli" style={{ width: `${Math.round(l.ratio * 100)}%` }} />
                    </span>
                    <span className="score-points">
                      {nombre(l.points)}
                      <span className="score-max"> / {nombre(l.max)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : (
          <p className="dessous-texte">{TEXTE_SCORE_ABSENT}</p>
        )}
      </DessousBloc>
      {fin ? null : (
        <DessousBloc titre="Ta fin">
          <p className="dessous-texte">{TEXTE_FIN_NON_NOMMEE}</p>
        </DessousBloc>
      )}
    </LesDessous>
  );

  return (
    /* ⟦SPEC-UI §3.7 · A-74⟧ Les trois habillages : la fenêtre porte la fin déterminée par le
       moteur (jamais un choix de menu) — La Rue, CEO, La Communauté. ⟦C48 · A-168 §3⟧ Le bandeau prend
       la couleur de la fin (`fins.css`), les deux suites de PLAN §4.13 vivent dans le pied fixe : elles
       restent sous les yeux quelle que soit la longueur du bilan. ⟦C61 · A-178⟧ La mise en scène, elle,
       est la cérémonie en pixel art (`Ceremonie.tsx`), la même pour les trois, dont la dernière ligne
       porte la fin : l'écran-ci n'est plus qu'un titre, la plaque, le récit en dessous, deux boutons. */
    <EcranFenetre
      titre="Le million"
      icone="🏆"
      badge={state.mode === "hardcore" ? "hardcore" : undefined}
      large
      className={`fenetre-victoire victoire--${state.ending ?? "la_rue"}${apresCeremonie ? " apres-ceremonie" : ""}`}
      pied={
        /* PLAN §4.13 : deux suites, et une seule fois dans la vie d'une run. */
        <div className="victoire-suites">
          <button className="bouton primaire grand" type="button" disabled={pending} onClick={() => void continuer()}>
            🏁 Continuer — viser le 100 %
            <span className="cout">
              paliers {PALIERS_POST_1M.map(nombreCourt).join(" · ")} · {noeudsTotal - noeudsPris} nœuds restants
            </span>
          </button>
          <button className="bouton grand" type="button" disabled={pending} onClick={() => void clore()}>
            📜 Clore la run — le mémorial
            <span className="cout">la chaîne s'arrête ici, la plaque reste</span>
          </button>
        </div>
      }
    >
      {/* ⟦SPEC-UI §3.7 / PLAN §4.13⟧ La plaque partageable : la même image que celle de la cérémonie, mais
          celle-ci sort du jeu. Elle est en tête d'écran parce que c'est elle, la récompense — le récit,
          le score et les deux suites viennent après. ⟦C61 · A-178⟧ Plus de scène en fil de fer au-dessus
          d'elle : la cérémonie en pixel art vient de se jouer, et se rejoue à la demande (plus bas). */}
      <PlaqueCanvas
        nomChaine={run.channelName}
        abonnes={Math.round(abonnesGraves)}
        jours={jourSimule(state.clock_h)}
        videos={state.counters.videos_published}
        score={victoire?.score ?? null}
        rang={rang}
        finTitre={fin?.titre ?? null}
        hardcore={state.mode === "hardcore"}
      />

      {/* ⟦C61 · A-178⟧ La narration RESTE à l'écran (A-174, décision de Matt), mise en page en dessous de
          la plaque : une phrase d'accroche en grand, puis « Ta fin » dans son encart, puis l'arc de
          rédemption. Les mots sont ceux d'avant, mot pour mot. */}
      <section className="victoire-recit" aria-label="Le récit de ta fin">
        <p className="victoire-lead" data-narration="fin">
          {nombre(abonnesGraves)} abonnés au jour {jourSimule(state.clock_h)}, en{" "}
          {state.counters.videos_published} vidéos. Une rue porte ton nom, et quelque part
          quelqu'un vient de cliquer sur ta toute première.
        </p>

        {fin ? (
          <div className="victoire-fin victoire-encart" data-narration="fin">
            <h2 className="bloc-titre">Ta fin — {fin.titre}</h2>
            <p>{fin.texte}</p>
          </div>
        ) : (
          /* ⟦A-71⟧ La cérémonie existe désormais et vient de se jouer ; ce qui manque encore, c'est que
             le moteur nomme LAQUELLE des trois fins est la tienne (M3.9). Une phrase à l'écran, le
             texte d'origine dans les dessous : on dit ce qui est vrai aujourd'hui. */
          <p className="ligne-info discret">{PHRASE_FIN_NON_NOMMEE}</p>
        )}

        {/* ⟦A-20 · A-74⟧ L'arc de rédemption, RACONTÉ : « il a eu un bad buzz, il est très descendu, et
            il a quand même gagné ». Les cicatrices sont relues, jamais recalculées ; une victoire sans
            chute n'affiche rien. C'est de la narration : elle reste (décision de Matt). */}
        {arcDeRedemption(state) ? (
          <div className="victoire-redemption victoire-encart" data-narration="fin">
            <h2 className="bloc-titre">Et pourtant.</h2>
            <p>
              Cette chaîne n'a pas fait une ligne droite : il y a eu{" "}
              {tracesDeLaChute(state).join(", ")}. Le million d'aujourd'hui, c'est celui de
              quelqu'un qui est resté. C'est ça, l'arc de rédemption — pas une mécanique, une
              trajectoire.
            </p>
          </div>
        ) : null}
      </section>

      {victoire?.score != null ? (
        <p className="ligne-info">
          Score gravé : <strong>{nombre(victoire.score)}</strong> / 1 000.
        </p>
      ) : null}

      <p className="ligne-info discret">
        Objectif {nombreCourt(OBJECTIF)} atteint · arbre {noeudsPris}/{noeudsTotal} nœuds ·{" "}
        {state.mode === "hardcore" ? "hardcore" : "mode normal"}
      </p>

      {/* ⟦A-71⟧ La deuxième porte de la cérémonie. Elle est là même quand la première ne s'est
          pas ouverte (mémoire d'interface indisponible, run reprise sur une autre machine) : un
          écran ne doit jamais être atteignable par un seul chemin. */}
      <div className="dessous-pose">
        <button className="bouton discret" type="button" onClick={() => setEtatCeremonie("joue")}>
          🎀 Revoir l'inauguration
        </button>
        {dessous}
      </div>
    </EcranFenetre>
  );
}
